import { useCallback, useEffect, useRef, useState } from 'react';
import { t } from 'react-switch-lang';
import { SubmitContactUs } from './SubmitContactUs';
import { setScreenReaderAlert } from './Accessibility';
import {
  events,
  trackGoogleAnalyticsEvent,
} from './GoogleAnalytics';

/**
 * Returns a promise that resolves with the reCAPTCHA token, or rejects when either the reCAPTCHA
 * challenge is prompted, or aborted by the user. (depending on the `rejectWhen` parameter)
 * Make sure to call ref.current.reset() after the token is consumed by the API. Once reCAPTCHA
 * is executed, this function will always resolve with the same token until reCAPTCHA is reset.
 *
 * @param {*} reCaptchaRefValue the `.current` value of the ref passed into the ReCAPTCHA component
 * @param {'CANCEL'|'CHALLENGE'} rejectWhen determines when the promise should reject
 *  - `CANCEL` promise rejects when user closes the challenge window
 *  - `CHALLENGE` promise rejects when the challenge window opens
 * @param {() => *} [onAbort] callback to execute when user aborts reCAPTCHA challenge
 * @returns {Promise<string>} a promise that resolves to the reCAPTCHA token, or rejects
 */
async function executeReCaptcha(reCaptchaRefValue, rejectWhen, onAbort) {
  return new Promise((resolve, reject) => {
    let mutationObserver;
    // find the reCAPTCHA challenge window
    const list = document.querySelectorAll("iframe[src*='google.com/recaptcha/api2/bframe']");
    if (list.length > 0) {
      const reCaptchaWindow = list[list.length - 1].parentNode.parentNode;

      mutationObserver = new MutationObserver(([{ target }], observer) => {
        if (target.style.opacity === '1') { // challenge window opened
          if (rejectWhen === 'CHALLENGE') reject();
        }
        if (target.style.opacity === '0') { // challenge window closed by clicking away
          onAbort?.();
          observer.disconnect();
          if (rejectWhen === 'CANCEL') reject();
        }
      }).observe(reCaptchaWindow, { attributeFilter: ['style'] });
    }

    const reCaptchaToken = reCaptchaRefValue.getValue();

    if (reCaptchaToken) {
      mutationObserver?.disconnect();
      resolve(reCaptchaToken);
    } else {
      reCaptchaRefValue.executeAsync()
        .then(resolve)
        .catch(reject)
        .finally(() => mutationObserver?.disconnect());
    }
  });
}

function useContactFormSubmit() {
  const successMessageRef = useRef();
  const errorMessageRef = useRef();
  const reCaptchaRef = useRef(null);
  const [error, setError] = useState('');
  const [showError, setShowError] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  const dismissError = useCallback(() => {
    setShowError(false);
    setError('');
  }, []);

  const showReCaptchaError = useCallback(() => {
    setError(t('GoogleRecaptchaFailureMsg'));
    setShowError(true);
    setScreenReaderAlert(t('ScreenReader_ContactForm_Error'));
  }, []);

  const submitContactRequest = useCallback(async (data) => {
    dismissError();

    const googleAnalyticsProperties = {
      brand: data.Brand,
      brand_id: data.BrandID,
      language: data.Language,
      category: data.Category,
    };

    trackGoogleAnalyticsEvent(
      events.CONTACT_FORM_SUBMIT_ATTEMPT,
      googleAnalyticsProperties
    );

    const reCaptchaToken = await executeReCaptcha(
      reCaptchaRef.current,
      'CANCEL'
    ).catch(() => '');

    if (!reCaptchaToken) return false;

    try {
      await SubmitContactUs(data, reCaptchaToken);
      trackGoogleAnalyticsEvent(
        events.CONTACT_FORM_SUBMIT_SUCCESS,
        googleAnalyticsProperties
      );
      setSubmitted(true);
      setScreenReaderAlert(t('ScreenReader_ContactForm_Success'));
      return true;
    } catch {
      setError(t('Error_Something_Went_Wrong'));
      setShowError(true);
      setScreenReaderAlert(t('ScreenReader_ContactForm_Error'));
      return false;
    } finally {
      reCaptchaRef.current?.reset();
    }
  }, [dismissError]);

  useEffect(() => {
    if (!submitted || !successMessageRef.current) return;
    successMessageRef.current.scrollIntoView({ behavior: 'smooth', block: 'center' });
  }, [submitted]);

  useEffect(() => {
    if (!showError || !errorMessageRef.current) return;
    errorMessageRef.current.scrollIntoView({ behavior: 'smooth', block: 'center' });
  }, [showError]);

  return {
    dismissError,
    error,
    errorMessageRef,
    reCaptchaRef,
    showReCaptchaError,
    showError,
    submitted,
    submitContactRequest,
    successMessageRef,
  };
}

export default useContactFormSubmit;
